import { discountPillParts, formatDiscountPill } from 'src/utils/coverPhoto'

/** SpotMe's first-month discount (`getDiscount`) or an operator's own offer
 * (`operatorPromoPill`) — never both: they are gated on different deal types. */
export type PromoBadgeContent =
  | { kind: 'firstMonth'; percentage: number; minMonths: number }
  | { kind: 'operator'; text: string }

interface PromoBadgeProps {
  promo: PromoBadgeContent
  /** `overlay` sits on a photo (shadow, capped to the photo's width);
   * `inline` sits in a card's copy column next to other pills. */
  placement: 'overlay' | 'inline'
  className?: string
}

/**
 * THE promotion badge for every listing card and the listing header — one
 * look, so a renter learns it once (Daniel, 2026-10-26: promotions should
 * be impossible to miss). Solid «rosa mexicano» (`promo-*`), never a status
 * colour.
 *
 * Wording stays the canonical pill (`formatDiscountPill`, docs/first-month-
 * discount.md): «50% 1er mes · 6+ meses». The badge is ALWAYS one line — it
 * never wraps (Jules, 2026-10-28: a two-line rosa block on a photo reads as
 * broken). It needs ~146–156px at 10px (172 at 11px), so it only goes where
 * that width exists: a card's full-width footer (`CardFactsStrip`), the wide
 * grid-card photo, the listing column. Never on the 130px search-card photo.
 * An operator promo is the operator's own wording, flagged as such in the
 * tooltip — SpotMe never presents it as its own.
 */
export default function PromoBadge({
  promo,
  placement,
  className = ''
}: PromoBadgeProps) {
  const isOverlay = placement === 'overlay'
  const shape = `inline-flex max-w-full shrink-0 items-center gap-1 rounded-md bg-promo-600 px-1.5 py-0.5 whitespace-nowrap text-white-500 md:px-2 ${
    isOverlay ? 'shadow-[0_2px_8px_-2px_rgba(18,38,63,0.35)]' : ''
  } ${className}`
  const icon = (
    <i
      aria-hidden
      className="si si-discount shrink-0 text-[10px] md:text-[11px]"
    />
  )

  if (promo.kind === 'operator') {
    return (
      <span title="Promoción del operador" className={shape}>
        {icon}
        {/* `truncate` is the last-resort guard, not the layout: every host
            container is sized for the longest pill (`operatorPromoPill`). */}
        <span className="min-w-0 truncate text-[10px] leading-tight font-semibold md:text-[11px]">
          {promo.text}
        </span>
      </span>
    )
  }

  const { headline, condition } = discountPillParts(promo)
  return (
    <span className={shape}>
      {icon}
      <span className="sr-only">{formatDiscountPill(promo)}</span>
      {/* The condition is its own darker chip, so the minimum stay reads as
          part of the offer, not as a separate fact. */}
      <span aria-hidden className="flex items-center gap-1 leading-tight">
        <span className="text-[10px] font-bold whitespace-nowrap md:text-[11px]">
          {headline}
        </span>
        <span className="rounded-sm bg-promo-700 px-1 text-[10px] font-semibold whitespace-nowrap md:text-[11px]">
          {condition}
        </span>
      </span>
    </span>
  )
}

/** Builds the badge content from the two card-level sources, or null. */
export function promoBadgeContent(
  discount: { percentage: number; minMonths: number } | null,
  operatorPromoText: string | null
): PromoBadgeContent | null {
  if (discount) {
    return {
      kind: 'firstMonth',
      percentage: discount.percentage,
      minMonths: discount.minMonths
    }
  }
  if (operatorPromoText) return { kind: 'operator', text: operatorPromoText }
  return null
}
